import classNames from 'classnames';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Tag } from '@flixbus/honeycomb-react';

import BreakdownCard from 'common/BreakdownCard';
import RunItem from 'common/RunItem';
import StatusIcon from 'common/StatusIcon';
import { getTagAppearanceProp } from 'utils/assigmentStatusToTagVariant';

import { ASSIGNMENT_DETAILS_SIDEBAR_TEXT } from '../text';
import RideBlock from './RideBlock';

import styles from './styles.module.css';

const getStationName = (station) => station?.name ?? station?.code ?? null;

const getRouteTitle = (sequences) => {
	const firstSequence = sequences[0];
	const lastSequence = sequences[sequences.length - 1];
	const fromName = getStationName(firstSequence?.fromStation);
	const toName = getStationName(lastSequence?.toStation);

	if (!fromName || !toName) return null;

	return `${fromName} → ${toName}`;
};

const getSequenceKey = (sequence, index) =>
	sequence?.ride?.id ??
	[
		sequence?.fromStation?.id ?? sequence?.fromStation?.code,
		sequence?.toStation?.id ?? sequence?.toStation?.code,
		index,
	].join('-');

const SequenceDetails = ({ result, currentRideId, externalBreakdown }) => {
	const navigate = useNavigate();

	if (!result) return null;

	const {
		crewMember,
		run,
		job,
		status,
		workAssignmentSequences,
		breakdown: resultBreakdown,
	} = result;
	const breakdown = resultBreakdown ?? externalBreakdown ?? null;
	const sequences = workAssignmentSequences ?? [];
	const isMultiRide = sequences.length > 1;
	const routeTitle = getRouteTitle(sequences);
	const timezone = sequences[0]?.fromStation?.timezone;

	const renderDriverDetails = () => {
		const isDriverAssigned = crewMember && Object.hasOwn(crewMember, 'fullName');
		return (
			<>
				<div className={styles.bold}>{ASSIGNMENT_DETAILS_SIDEBAR_TEXT.assignedCrewMember}</div>
				<div className={styles.driver}>
					{isDriverAssigned && (
						<span
							className={styles.name}
							onClick={() => navigate(`/crew-management/${crewMember.id}`)}
						>
							{crewMember.fullName}
						</span>
					)}
					{isDriverAssigned && status && (
						<Tag small {...getTagAppearanceProp(status)}>
							<StatusIcon status={status} />
							{status}
						</Tag>
					)}
					{!isDriverAssigned && (
						<div className={styles.nodriver}>
							{ASSIGNMENT_DETAILS_SIDEBAR_TEXT.noCrewMemberAssigned}
						</div>
					)}
				</div>
			</>
		);
	};

	return (
		<div className={styles.assigment}>
			{routeTitle && <div className={classNames(styles.bold, styles.routeTitle)}>{routeTitle}</div>}

			<div className={styles.header}>
				<div className={styles.runjob}>
					<span className={styles.bold}>{ASSIGNMENT_DETAILS_SIDEBAR_TEXT.run}</span>{' '}
					<div>
						<RunItem runLabel="" run={run} isTag withoutLabel smallTag />
					</div>
				</div>
				<div className={styles.runjob}>
					<span className={styles.bold}>{ASSIGNMENT_DETAILS_SIDEBAR_TEXT.job}</span>{' '}
					<Tag small display="outlined">
						{` ${job?.code ?? 'N/A'} `}
					</Tag>
				</div>
			</div>

			{renderDriverDetails()}

			{isMultiRide && (
				<div className={styles.multiRideBanner}>
					<span className={styles.bold}>
						{ASSIGNMENT_DETAILS_SIDEBAR_TEXT.multiRideWorkAssignment}
					</span>
				</div>
			)}

			{breakdown && (
				<BreakdownCard
					breakdown={breakdown}
					timezone={timezone}
					className={styles.breakdownCard}
					stackedTime
					sequences={sequences}
				/>
			)}

			<div className={styles.details}>
				{sequences.map((seq, idx) => (
					<RideBlock
						key={getSequenceKey(seq, idx)}
						rideNumber={idx + 1}
						ride={seq.ride}
						line={seq.line}
						fromStation={seq.fromStation}
						toStation={seq.toStation}
						extraActivities={seq.extraActivities}
						workAssignments={seq.workAssignments}
						isStandalone={!isMultiRide}
						isCurrent={isMultiRide && seq.ride?.id === currentRideId}
					/>
				))}
			</div>
		</div>
	);
};

export default SequenceDetails;
